import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icons from "../icons";

/* CortexQA loader.
 *
 * One component, three faces, because a loader that depicts a specific activity
 * lies when it is reused for something else:
 *
 *   variant="guide"  a requirements document being scanned, Epic and Story
 *                    cards stacking up out of it — for Create Epic/Story cards
 *   variant="xray"   the agent thinking while test steps write themselves
 *                    underneath it — for Build Xray tests
 *   variant="spark"  abstract, the app's own Spark mark — the default, usable
 *                    over any operation and small enough to sit inline
 *
 * Usage:
 *   <Loader.Inline />                       16px, drops into a button or row
 *   <Loader.Block label="Loading epics…" /> centred in a panel or empty state
 *   <Loader.Overlay variant="guide" />      blocking dialog over a dimmed page
 *   <Loader.Overlay variant="xray" />
 *   <Loader.Overlay />                      the spark default
 *
 * The Overlay is deliberately NOT dismissible: there is no close button, the
 * backdrop ignores clicks, and Escape is swallowed so it cannot close a parent
 * dialog underneath it. Cancelling midway through a generation run would leave
 * half-created Jira issues behind.
 *
 * Animations are CSS, defined once in the document head rather than in
 * tailwind.config.js, so this file is self-contained and can be dropped
 * anywhere. The whole set collapses to a static frame under
 * prefers-reduced-motion.
 */

export const STYLE_ID = "cqa-loader-styles";

/* Exported so the keyframes can be asserted on directly instead of fishing the
   injected <style> element back out of the document. */
export const ANIMATION_CSS = `
.cqa-ld * { transform-box: fill-box; transform-origin: center; }

@keyframes cqa-ld-spin   { to { transform: rotate(360deg); } }
@keyframes cqa-ld-spin-r { to { transform: rotate(-360deg); } }
@keyframes cqa-ld-halo   { 0%,100% { transform: scale(.85); opacity:.22 } 50% { transform: scale(1.5); opacity:0 } }
@keyframes cqa-ld-breathe{ 0%,100% { opacity:1 } 50% { opacity:.5 } }

/* guide: scan bar sweeping the document */
@keyframes cqa-ld-scan   { 0% { transform: translateY(0) } 50% { transform: translateY(78px) } 100% { transform: translateY(0) } }
/* guide: content carried from the document into a card */
@keyframes cqa-ld-carry  { 0% { transform: translate(0,0); opacity:0 }
                           15% { opacity:1 }
                           55% { transform: translate(22px,0); opacity:0 }
                           100% { transform: translate(22px,0); opacity:0 } }
/* guide + xray: a card or a line arriving */
@keyframes cqa-ld-arrive { 0%,8% { opacity:0; transform: translateX(-6px) }
                           18%,88% { opacity:1; transform: translateX(0) }
                           100% { opacity:0; transform: translateX(0) } }
/* guide: indeterminate progress bar */
@keyframes cqa-ld-bar    { 0% { transform: translateX(0) } 50% { transform: translateX(120px) } 100% { transform: translateX(0) } }

/* xray: the head tilting as it thinks */
@keyframes cqa-ld-tilt   { 0%,100% { transform: rotate(-3deg) } 50% { transform: rotate(3deg) } }
/* xray: eye blink */
@keyframes cqa-ld-blink  { 0%,4%,10%,100% { transform: scaleY(1) } 6%,8% { transform: scaleY(.18) } }
/* xray: mouth */
@keyframes cqa-ld-talk   { 0%,100% { transform: scaleX(1) } 50% { transform: scaleX(.55) } }
/* xray: thought dots rising */
@keyframes cqa-ld-rise   { 0% { transform: translateY(4px); opacity:0 }
                           40% { opacity:1 }
                           100% { transform: translateY(-14px); opacity:0 } }
/* xray: a step line writing itself left to right */
@keyframes cqa-ld-write  { 0%,5% { transform: scaleX(0) }
                           28%,90% { transform: scaleX(1) }
                           100% { transform: scaleX(0) } }

.cqa-ld-spin    { animation: cqa-ld-spin 1.3s linear infinite; }
.cqa-ld-orbit   { animation: cqa-ld-spin-r 3.4s linear infinite; }
.cqa-ld-halo    { animation: cqa-ld-halo 2.2s ease-in-out infinite; }
.cqa-ld-breathe { animation: cqa-ld-breathe 1.6s ease-in-out infinite; }
.cqa-ld-scan    { animation: cqa-ld-scan 3.4s ease-in-out infinite; }
.cqa-ld-carry   { animation: cqa-ld-carry 3.4s ease-in-out infinite; }
.cqa-ld-arrive  { animation: cqa-ld-arrive 3.4s ease-in-out infinite; }
.cqa-ld-bar     { animation: cqa-ld-bar 1.8s ease-in-out infinite; }
.cqa-ld-tilt    { animation: cqa-ld-tilt 3.2s ease-in-out infinite; }
.cqa-ld-blink   { animation: cqa-ld-blink 3.4s ease-in-out infinite; }
.cqa-ld-talk    { animation: cqa-ld-talk 2.4s ease-in-out infinite; }
.cqa-ld-rise    { animation: cqa-ld-rise 1.8s ease-out infinite; }
.cqa-ld-write   { transform-origin: left center; animation: cqa-ld-write 3.6s ease-in-out infinite; }

.cqa-ld-d1 { animation-delay: .3s }
.cqa-ld-d2 { animation-delay: .6s }
.cqa-ld-d3 { animation-delay: .9s }
.cqa-ld-d4 { animation-delay: 1.2s }

/* Finished states animate once, on arrival, and then hold still — looping
   motion is what tells a user something is still in progress. */
@keyframes cqa-ld-pop    { 0% { transform: scale(0); opacity: 0 } 70% { transform: scale(1.15) } 100% { transform: scale(1); opacity: 1 } }
@keyframes cqa-ld-settle { 0% { transform: translateX(-6px); opacity: 0 } 100% { transform: translateX(0); opacity: 1 } }

.cqa-ld-pop    { animation: cqa-ld-pop .45s ease-out .25s both; }
.cqa-ld-settle { animation: cqa-ld-settle .35s ease-out both; }

/* Motion is decoration here; the text carries the message. */
@media (prefers-reduced-motion: reduce) {
  .cqa-ld [class*="cqa-ld-"] { animation: none !important; }
  .cqa-ld .cqa-ld-write,
  .cqa-ld .cqa-ld-arrive,
  .cqa-ld .cqa-ld-carry { opacity: 1 !important; transform: none !important; }
}
`;

/* Injected once, on first mount, so the file carries its own animations
   without touching the shared Tailwind config. */
function useLoaderStyles() {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const el = document.createElement("style");
    el.id = STYLE_ID;
    el.textContent = ANIMATION_CSS;
    document.head.appendChild(el);
  }, []);
}

/* ------------------------------------------------------------------ */
/*  Artwork                                                            */
/* ------------------------------------------------------------------ */

/* Every drawing is Lucide icons laid out with Tailwind; the motion is the
   cqa-ld-* classes above. Each is aria-hidden: the text carries the message. */
const DOT = "absolute block rounded-full";

/* The app's own Spark mark, orbited. Abstract on purpose: this one is shown
   over operations of every kind, so it must not claim to be doing anything
   in particular. */
function SparkArt({ size = 112 }) {
  const halo = Math.round(size * 0.42);
  return (
    <div className="cqa-ld relative flex-none" style={{ width: size, height: size }} aria-hidden="true">
      <Icons.Ring size={size} strokeWidth={0.7} className="absolute inset-0 text-brand-100" />
      <span
        className={`${DOT} cqa-ld-halo bg-brand-600`}
        style={{ width: halo, height: halo, left: (size - halo) / 2, top: (size - halo) / 2 }}
      />
      <Icons.Spinner size={size} strokeWidth={0.8} className="cqa-ld-spin absolute inset-0 text-brand-600" />
      <span className="cqa-ld-orbit absolute inset-0">
        <span className={DOT} style={{ width: 8, height: 8, left: size / 2 - 4, top: size / 12 - 4, backgroundColor: "#7C3AED" }} />
      </span>
      <Icons.Spark
        size={halo}
        className="cqa-ld-breathe absolute text-brand-600"
        style={{ left: (size - halo) / 2, top: (size - halo) / 2 }}
      />
    </div>
  );
}

/* A requirements guide being read, with Epic and Story cards stacking up out
   of it. Deliberately a document and not a browser window — this runs while
   the AI reads a PlantOps help topic, not while it explores a page. */
function GuideCard({ Icon, top, left, bg, border, chip, bar, className }) {
  return (
    <div
      className={`absolute flex items-center gap-2 rounded-lg border px-2 ${className}`}
      style={{ top, left, right: 4, height: 30, backgroundColor: bg, borderColor: border }}
    >
      <Icon />
      <span className="block h-2 rounded-full" style={{ width: bar, backgroundColor: chip }} />
    </div>
  );
}

function GuideScanArt() {
  return (
    <div className="cqa-ld relative flex-none" style={{ width: 330, height: 125 }} aria-hidden="true">
      {/* the guide, with a scan bar sweeping it */}
      <Icons.Report size={118} strokeWidth={0.9} className="absolute text-slate-400" style={{ left: -12, top: 3 }} />
      <span className="absolute overflow-hidden" style={{ left: 8, top: 13, width: 70, height: 98 }}>
        <span
          className="cqa-ld-scan absolute inset-x-0 top-0 block h-5"
          style={{ background: "linear-gradient(to bottom, transparent, rgb(var(--brand-500) / .55), transparent)" }}
        />
      </span>

      {/* content carried across into the cards */}
      {[37, 65, 92].map((top, i) => (
        <span
          key={top}
          className={`${DOT} cqa-ld-carry ${["", "cqa-ld-d2", "cqa-ld-d4"][i]}`}
          style={{ left: 94, top, width: 8, height: 8, backgroundColor: "#7C3AED" }}
        />
      ))}

      {/* the epic, then its stories */}
      <GuideCard Icon={Icons.JiraEpic} top={6} left={125} bg="#F5F3FF" border="#C4B5FD" chip="#C4B5FD" bar={105} className="cqa-ld-arrive" />
      {/* Story cards use blue to match Jira / the Story Cards column in the
          Requirement Board, so the loader previews the colours to come. */}
      <GuideCard Icon={Icons.JiraStory} top={47} left={140} bg="#EFF6FF" border="#93C5FD" chip="#93C5FD" bar={88} className="cqa-ld-arrive cqa-ld-d2" />
      <GuideCard Icon={Icons.JiraStory} top={88} left={140} bg="#EFF6FF" border="#93C5FD" chip="#93C5FD" bar={100} className="cqa-ld-arrive cqa-ld-d4" />
    </div>
  );
}

/* The step lines under the bot: writing themselves while it works, ticked
   once it is done. */
const STEP_WIDTHS = [88, 125, 105];

/* The agent thinking, with test steps writing themselves underneath. The steps
   matter: they keep a friendly bot head from reading as a chat assistant by
   showing what it is actually producing. */
function BotArt() {
  return (
    <div className="cqa-ld relative flex-none" style={{ width: 212, height: 187 }} aria-hidden="true">
      <div className="cqa-ld-tilt absolute" style={{ left: 50, top: 6, width: 112, height: 112 }}>
        <Icons.Bot size={112} strokeWidth={1.4} className="text-brand-600" />
        {/* thoughts */}
        {[
          [94, 30, 5, "#C4B5FD", ""],
          [104, 21, 6, "#A78BFA", "cqa-ld-d1"],
          [115, 10, 8, "#7C3AED", "cqa-ld-d2"],
        ].map(([left, top, d, color, delay]) => (
          <span key={left} className={`${DOT} cqa-ld-rise ${delay}`} style={{ left, top, width: d, height: d, backgroundColor: color }} />
        ))}
      </div>

      {/* the steps it is writing */}
      {STEP_WIDTHS.map((w, i) => (
        <span
          key={w}
          className={`absolute block h-2 rounded-full bg-brand-200 cqa-ld-write ${["", "cqa-ld-d2", "cqa-ld-d4"][i]}`}
          style={{ left: 44, top: 140 + i * 15, width: w }}
        />
      ))}
    </div>
  );
}

/* The same agent, finished.
 *
 * A success state must not reuse the working animation: the same bot still
 * tilting and thinking reads as "it is thinking" and leaves the user unsure
 * whether anything happened. What makes "done" unambiguous is a *change of
 * state*: the bot holds still, the thought bubbles are gone, the step lines it
 * was writing are complete, each with a tick, and a check badge sits on its
 * head. It is deliberately still: motion signals work in progress, so a
 * finished state only animates once, on arrival.
 */
function BotDoneArt() {
  return (
    <div className="cqa-ld relative flex-none" style={{ width: 212, height: 187 }} aria-hidden="true">
      <Icons.Bot size={112} strokeWidth={1.4} className="absolute text-brand-600" style={{ left: 50, top: 6 }} />

      {/* the badge that says the work landed */}
      <span
        className="cqa-ld-pop absolute flex items-center justify-center rounded-full bg-brand-600"
        style={{ left: 132, top: 22, width: 28, height: 28 }}
      >
        <Icons.Check size={18} strokeWidth={3} color="#fff" />
      </span>

      {/* the steps, written and ticked */}
      {STEP_WIDTHS.map((w, i) => (
        <span
          key={w}
          className={`cqa-ld-settle absolute flex items-center gap-2 ${["", "cqa-ld-d1", "cqa-ld-d2"][i]}`}
          style={{ left: 36, top: 136 + i * 15 }}
        >
          <Icons.Check size={14} strokeWidth={3} className="text-brand-600" />
          <span className="block h-2 rounded-full bg-brand-200" style={{ width: w - 10 }} />
        </span>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Variants                                                           */
/* ------------------------------------------------------------------ */

const VARIANTS = {
  spark: {
    Art: () => <SparkArt />,
    title: "Loading…",
    lines: ["Please wait."],
    bar: false,
  },
  guide: {
    Art: GuideScanArt,
    title: "Reading your requirements guide…",
    lines: [
      "Drafting your Epics & Stories. This can take a moment —",
      "please wait, don't close this page.",
    ],
    bar: true,
  },
  xray: {
    Art: BotArt,
    title: "Writing your tests…",
    lines: ["The agent is authoring steps. Please wait."],
    bar: false,
  },
  "xray-done": {
    Art: BotDoneArt,
    title: "Your tests are written",
    lines: [],
    bar: false,
  },
};

/* Just the drawing, for callers that supply their own surrounding layout. */
function Art({ variant = "spark" }) {
  useLoaderStyles();
  const v = variantOf(variant);
  return <v.Art />;
}

function variantOf(name) {
  return VARIANTS[name] || VARIANTS.spark;
}

/* ------------------------------------------------------------------ */
/*  Inline — replaces a bare spinner in a button or a row              */
/* ------------------------------------------------------------------ */

function Inline({ size = 16, className = "" }) {
  useLoaderStyles();
  return (
    <span className={["cqa-ld inline-flex flex-none", className].filter(Boolean).join(" ")}>
      <Icons.Spinner
        width={size}
        height={size}
        strokeWidth={2.5}
        className="cqa-ld-spin"
        role="status"
        aria-label="Loading"
      />
    </span>
  );
}

/* ------------------------------------------------------------------ */
/*  Block — centred inside a panel or an empty state                   */
/* ------------------------------------------------------------------ */

function Block({ variant = "spark", label, sublabel, className = "" }) {
  useLoaderStyles();
  const v = variantOf(variant);
  const title = label !== undefined ? label : v.title;
  const lines = sublabel !== undefined ? [sublabel] : v.lines;

  return (
    <div
      className={[
        "flex flex-col items-center justify-center gap-3 px-6 py-10 text-center",
        className,
      ].filter(Boolean).join(" ")}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <v.Art />
      {title && <p className="text-sm font-semibold text-slate-900">{title}</p>}
      {lines.filter(Boolean).map((line, i) => (
        <p key={i} className="-mt-2 text-xs leading-relaxed text-slate-500">
          {line}
        </p>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Overlay — the blocking dialog                                      */
/* ------------------------------------------------------------------ */

/* Not dismissible, and that has to be enforced rather than merely implied:
 *  - no close button, and the backdrop ignores clicks
 *  - Escape is captured and stopped, so it cannot reach a parent dialog and
 *    close that instead (every modal in this app listens for Escape)
 *  - Tab is swallowed; there is nothing to focus in here and focus must not
 *    wander into the dimmed page behind
 *  - background scrolling is locked while it is up
 *
 * `delay` holds the overlay back briefly so an operation that turns out to be
 * fast doesn't produce a disorienting flash of dialog. Pass 0 to show at once.
 */
function Overlay({
  variant = "spark",
  label,
  sublabel,
  delay = 150,
  className = "",
  children,
}) {
  useLoaderStyles();
  const [visible, setVisible] = useState(delay === 0);
  const cardRef = useRef(null);

  useEffect(() => {
    if (delay === 0) return undefined;
    const t = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(t);
  }, [delay]);

  /* Escape never gets through — it would close a dialog underneath instead of
     this one. Tab is kept inside: the overlay may carry a real action such as
     "Stop run", and swallowing Tab outright would make that button reachable
     only with a mouse. */
  useEffect(() => {
    if (!visible) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (e.key !== "Tab") return;

      // Nothing outside this dialog should receive focus while it is up.
      e.stopPropagation();
      const root = cardRef.current;
      const focusable = root
        ? root.querySelectorAll(
            'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          )
        : [];
      if (focusable.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === root)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || active === root)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [visible]);

  // Lock background scroll, restoring whatever was there before.
  useEffect(() => {
    if (!visible) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [visible]);

  useEffect(() => {
    if (visible) cardRef.current?.focus();
  }, [visible]);

  if (!visible) return null;

  const v = variantOf(variant);
  const title = label !== undefined ? label : v.title;
  const lines = sublabel !== undefined ? [sublabel] : v.lines;

  /* Rendered on <body>, not where it is used: inside the page, an ancestor
     with its own stacking or containing block (a transform, a filter, a
     sticky header's layer) clips a `fixed` element, which left a strip of
     the header uncovered at the top. */
  return createPortal(
    <div
      className={[
        // Above the app's other dialogs (z-50 and z-60):
        // this one blocks whatever is already open.
        "fixed inset-0 z-[70] flex items-center justify-center p-4",
        className,
      ].filter(Boolean).join(" ")}
    >
      <div className="absolute inset-0 bg-overlay/50 backdrop-blur-sm" />
      <div
        ref={cardRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-busy="true"
        aria-live="polite"
        aria-label={title || "Loading"}
        className="relative z-10 w-full max-w-md rounded-2xl bg-card px-8 py-9 text-center shadow-xl outline-none ring-1 ring-slate-200"
      >
        <div className="flex justify-center">
          <v.Art />
        </div>
        {title && (
          <p className="mt-6 text-base font-semibold text-slate-900">{title}</p>
        )}
        {lines.filter(Boolean).map((line, i) => (
          <p key={i} className="mt-1.5 text-[0.8125rem] leading-relaxed text-slate-500">
            {line}
          </p>
        ))}
        {v.bar && (
          <div className="mx-auto mt-5 h-1.5 w-52 overflow-hidden rounded-full bg-slate-200">
            <div className="h-1.5 w-20 rounded-full bg-brand-600 cqa-ld-bar" />
          </div>
        )}
        {/* Room for whatever the caller needs to say or offer alongside the
            wait: the step it has reached, an error, a way to stop. A long wait
            with nothing but an animation is indistinguishable from a hang. */}
        {children}
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */

const Loader = Inline;
Loader.Inline = Inline;
Loader.Block = Block;
Loader.Overlay = Overlay;
Loader.Art = Art;

export { Inline, Block, Overlay, Art, VARIANTS };
export default Loader;
